import { useAuthorizedQuery, useInternalUserQuery, } from "../api/authorized-query";
import { seedgreenAPI } from "../api/axios";
import { CustomerDTO } from "../dtos/CustomerDTO";
import { STALE_TIME_MEDIUM } from "../queries/query.constants";
import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
const CUSTOMER_STALE_TIME = STALE_TIME_MEDIUM;
export function useGetCustomerLogoUploadUrl() {
    const isAuthorized = useAuthorizedQuery();
    return useMutation({
        mutationFn: async (params) => {
            if (!isAuthorized.value)
                throw new Error("Not authorized");
            const photoRequests = params.photos.map((photo) => ({
                fileName: photo.name,
                mimeType: photo.mimeType,
            }));
            const response = await seedgreenAPI.post(`v2/customer/${params.customerId}/logo-upload-url`, photoRequests);
            return response.data;
        },
    });
}
export function useMyCustomer() {
    const query = useQuery({
        enabled: useAuthorizedQuery(),
        queryKey: ["customer-me"],
        queryFn: async () => (await seedgreenAPI.get("v2/Customer/me")).data,
        staleTime: CUSTOMER_STALE_TIME,
        select: (data) => {
            try {
                if (!data)
                    throw new Error("Customer not found");
                return new CustomerDTO(data);
            }
            catch (error) {
                console.error("query/customer select ERROR", error);
                return undefined;
            }
        },
    });
    return {
        myCustomer: query.data,
        ...query,
    };
}
export function useCustomers(includeInactive = false) {
    const query = useQuery({
        enabled: useInternalUserQuery(),
        queryKey: ["customers", includeInactive],
        queryFn: async () => (await seedgreenAPI.get("v2/Customer", {
            params: { includeInactive },
        })).data,
        staleTime: CUSTOMER_STALE_TIME,
        select: (data) => {
            try {
                if (!data)
                    throw new Error("Customer not found");
                return data.map((customer) => new CustomerDTO(customer));
            }
            catch (error) {
                console.error("query/customer select ERROR", error);
            }
        },
    });
    return {
        customers: query.data,
        ...query,
    };
}
export function useCreateCustomer() {
    const queryClient = useQueryClient();
    const isAuthorized = useInternalUserQuery();
    return useMutation({
        mutationFn: async (customer) => {
            if (!isAuthorized.value)
                throw new Error("Not authorized");
            const response = await seedgreenAPI.post("v2/Customer", customer);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["customers"] });
        },
    });
}
/**
 * Patch customer details using JSON Patch operations.
 * Follows the same pattern as usePatchPlanting.
 */
export function usePatchCustomer() {
    const queryClient = useQueryClient();
    const isAuthorized = useInternalUserQuery();
    const { mutateAsync, isPending, error } = useMutation({
        mutationFn: async ({ id, patches, }) => {
            if (!isAuthorized.value)
                throw new Error("Not authorized");
            await seedgreenAPI.patch(`v2/Customer/${id}`, patches);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["customers"] });
            queryClient.invalidateQueries({ queryKey: ["customer-me"] });
        },
    });
    return {
        patchCustomer: mutateAsync,
        isPending,
        error,
    };
}
